import { useState } from "react"
import { Box, Card, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js"
import { Bar } from "react-chartjs-2"
import { SegmentedPeriod } from "components/layout/segmented-period"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const C = {
  text: "#12131A",
  muted: "#65748B",
  brand: "#F3DA00",
  border: "#E6E8F0",
  surface: "#FFFFFF",
  grey: "#65748B",
}

const fmt = n => (typeof n === "number" ? n.toLocaleString() : n)
const fmtYoY = v => (v === null || v === undefined ? "–" : `${v > 0 ? "+" : ""}${v}%`)

// Translucent version of a series colour, for the partial (MTD) month's bars.
const fade = hex => {
  const m = { "#12131A": "18,19,26", "#F3DA00": "243,218,0", "#65748B": "101,116,139" }[hex]
  return `rgba(${m},0.4)`
}
const fills = (n, p, hex) => Array.from({ length: n }, (_, i) => (i === p ? fade(hex) : hex))
const borders = (n, p, hex) => Array.from({ length: n }, (_, i) => (i === p ? hex : "transparent"))
const widths = (n, p) => Array.from({ length: n }, (_, i) => (i === p ? 1.5 : 0))

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

const Panel = ({ children, sx }) => (
  <Card elevation={0} sx={{ bgcolor: C.surface, border: `1px solid ${C.border}`, borderRadius: 3, p: 2, height: "100%", ...sx }}>
    {children}
  </Card>
)

const PanelHeader = ({ title, subtitle, action }) => (
  <Box sx={{ mb: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: C.text }}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: C.muted, display: "block", mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
)

const PartialNote = ({ show }) =>
  show ? (
    <Typography variant="caption" sx={{ color: C.muted, display: "block", mt: 0.75, fontSize: 11 }}>
      Last month (faded bar) is month-to-date and excluded from the tiles above.
    </Typography>
  ) : null

// ---------------------------------------------------------------------------
// KPI tiles
// ---------------------------------------------------------------------------

export const KpiTile = ({ label, value, subtext }) => (
  <Panel sx={{ p: 2 }}>
    <Typography variant="overline" sx={{ color: C.muted, letterSpacing: 0.8, fontSize: 11, lineHeight: 1.4, display: "block" }}>{label}</Typography>
    <Typography sx={{ fontSize: 29, fontWeight: 800, color: C.text, lineHeight: 1.1, letterSpacing: -0.5, fontVariantNumeric: "tabular-nums" }}>{fmt(value)}</Typography>
    {subtext && <Typography variant="caption" sx={{ color: C.muted, display: "block", mt: 0.75 }}>{subtext}</Typography>}
  </Panel>
)

export const ChannelLabelTile = ({ label }) => (
  <Panel sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: C.text }}>{label}</Typography>
  </Panel>
)

// One outbound channel (B2C/B2B) as a single card: coloured channel header,
// then Created | Despatched split-cell inside. Replaces the oversized B2C/B2B
// label tiles (per July 2026 product session — Lee/Anuj flagged those).
const ChannelHalf = ({ label, orders, units, meta }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: C.muted, mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 29, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }} noWrap>
      {fmt(orders)}
    </Typography>
    <Typography variant="caption" sx={{ color: C.muted, display: "block", mt: 0.75 }}>
      {fmt(units)} units · {meta}
    </Typography>
  </Box>
)

export const ChannelCard = ({ channel, dot, data }) => (
  <Panel>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: dot }} />
      <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.2, color: C.text }}>{channel}</Typography>
    </Box>
    <Box sx={{ display: "flex", gap: 2 }}>
      <ChannelHalf label="Created" orders={data.createdOrders} units={data.createdUnits} meta={`${data.unitsPerOrder} u/o · by creation date`} />
      <Divider orientation="vertical" flexItem sx={{ borderColor: C.border }} />
      <ChannelHalf label="Despatched" orders={data.despatchedOrders} units={data.despatchedUnits} meta={`${data.despatchedPct}% of created · by despatch date`} />
    </Box>
  </Panel>
)

// Split-cell KPI: shipment count and unit count side by side, both prominent
// (per July 2026 product session — units must not be small grey subtext).
export const SplitKpiTile = ({ label, shipments, units, subtext }) => (
  <Panel sx={{ p: 2 }}>
    <Typography variant="overline" sx={{ color: C.muted, letterSpacing: 0.8, fontSize: 11, lineHeight: 1.4, display: "block", mb: 0.75 }}>{label}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
      <Box sx={{ flex: 0.8, minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: C.muted, mb: 0.25 }}>Shipments</Typography>
        <Typography sx={{ fontSize: 29, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }} noWrap>
          {fmt(shipments)}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ borderColor: C.border }} />
      <Box sx={{ flex: 1.2, minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: C.muted, mb: 0.25 }}>Units</Typography>
        <Typography sx={{ fontSize: 29, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }} noWrap>
          {fmt(units)}
        </Typography>
      </Box>
    </Box>
    {subtext && <Typography variant="caption" sx={{ color: C.muted, display: "block", mt: 1 }}>{subtext}</Typography>}
  </Panel>
)

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

const baseOptions = stacked => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { position: "top", labels: { boxWidth: 12, boxHeight: 12, usePointStyle: true, font: { size: 12 } } },
    tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${Number(ctx.raw).toLocaleString()}` } },
  },
  scales: {
    x: { stacked, grid: { display: false }, ticks: { font: { size: 11 }, color: C.muted } },
    y: { stacked, beginAtZero: true, grid: { color: "#F1F3F7" }, ticks: { font: { size: 11 }, color: C.muted, callback: v => Number(v).toLocaleString() } },
  },
})

// Grouped Created (ink) vs Despatched (yellow) bars.
export const CreatedDespatchedChart = ({ title, subtitle, labels, partialIndex, rows }) => {
  const n = labels.length
  const p = partialIndex
  const data = {
    labels,
    datasets: [
      { label: "Created", data: rows.map(r => r.created), backgroundColor: fills(n, p, C.text), borderColor: borders(n, p, C.text), borderWidth: widths(n, p), borderRadius: 3 },
      { label: "Despatched", data: rows.map(r => r.despatched), backgroundColor: fills(n, p, C.brand), borderColor: borders(n, p, C.brand), borderWidth: widths(n, p), borderRadius: 3 },
    ],
  }
  return (
    <Panel>
      <PanelHeader title={title} subtitle={subtitle} />
      <Box sx={{ height: 220 }}>
        <Bar data={data} options={baseOptions(false)} />
      </Box>
      <PartialNote show={p >= 0} />
    </Panel>
  )
}

// Stacked B2C (yellow) / B2B (ink) with an Orders | Units toggle.
export const OutboundMixChart = ({ labels, partialIndex, mixChart }) => {
  const [measure, setMeasure] = useState("orders")
  const n = labels.length
  const p = partialIndex
  const b2c = mixChart.map(r => (measure === "orders" ? r.b2cOrders : r.b2cUnits))
  const b2b = mixChart.map(r => (measure === "orders" ? r.b2bOrders : r.b2bUnits))
  const data = {
    labels,
    datasets: [
      { label: "B2C", data: b2c, backgroundColor: fills(n, p, C.brand), borderColor: borders(n, p, C.brand), borderWidth: widths(n, p), borderRadius: 3, stack: "mix" },
      { label: "B2B", data: b2b, backgroundColor: fills(n, p, C.text), borderColor: borders(n, p, C.text), borderWidth: widths(n, p), borderRadius: 3, stack: "mix" },
    ],
  }
  return (
    <Panel>
      <PanelHeader
        title="Outbound Volume by Month — B2B vs B2C"
        subtitle="Created volume split by channel"
        action={<SegmentedPeriod value={measure} onChange={setMeasure} options={[{ key: "orders", label: "Orders" }, { key: "units", label: "Units" }]} />}
      />
      <Box sx={{ height: 220 }}>
        <Bar data={data} options={baseOptions(true)} />
      </Box>
      <PartialNote show={p >= 0} />
    </Panel>
  )
}

const INBOUND_COLORS = { lcl: C.brand, fcl20: C.text, fcl40: C.grey }

export const InboundChart = ({ data }) => {
  const n = data.labels.length
  const p = data.partialIndex
  const chartData = {
    labels: data.labels,
    datasets: [
      { label: "LCL", data: data.inboundByPeriod.map(d => d.lcl), backgroundColor: fills(n, p, INBOUND_COLORS.lcl), borderColor: borders(n, p, INBOUND_COLORS.lcl), borderWidth: widths(n, p), borderRadius: 3, stack: "in" },
      { label: "20ft FCL", data: data.inboundByPeriod.map(d => d.fcl20), backgroundColor: fills(n, p, INBOUND_COLORS.fcl20), borderColor: borders(n, p, INBOUND_COLORS.fcl20), borderWidth: widths(n, p), borderRadius: 3, stack: "in" },
      { label: "40ft FCL", data: data.inboundByPeriod.map(d => d.fcl40), backgroundColor: fills(n, p, INBOUND_COLORS.fcl40), borderColor: borders(n, p, INBOUND_COLORS.fcl40), borderWidth: widths(n, p), borderRadius: 3, stack: "in" },
    ],
  }
  return (
    <Panel>
      <PanelHeader title="Inbound shipments by month" subtitle="LCL, FCL 20ft & 40ft containers · closed receipts only" />
      <Box sx={{ height: 220 }}>
        <Bar data={chartData} options={baseOptions(true)} />
      </Box>
      <PartialNote show={p >= 0} />
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

const headCellSx = { fontWeight: 700, fontSize: 11, letterSpacing: 0.4, color: C.muted, textTransform: "uppercase", borderBottom: `1px solid ${C.border}`, py: 1 }
const cellSx = { fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}`, py: 1, fontVariantNumeric: "tabular-nums" }
const MonthCell = ({ row }) => (
  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
    {row.label}
    {row.partial && <Typography component="span" sx={{ color: C.muted, fontWeight: 400, fontSize: 11, ml: 0.5 }}>· MTD</Typography>}
  </TableCell>
)

export const OutboundTable = ({ data }) => (
  <Panel>
    <PanelHeader title="Outbound Orders Summary (B2C+B2B)" subtitle="By order creation date · YoY on created" />
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 460 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Month</TableCell>
            <TableCell sx={headCellSx} align="right">Created</TableCell>
            <TableCell sx={headCellSx} align="right">Despatched</TableCell>
            <TableCell sx={headCellSx} align="right">Units/Order</TableCell>
            <TableCell sx={headCellSx} align="right">YoY</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.outboundDetail.map(r => (
            <TableRow key={r.label}>
              <MonthCell row={r} />
              <TableCell sx={cellSx} align="right">{fmt(r.created)}</TableCell>
              <TableCell sx={cellSx} align="right">{fmt(r.despatched)}</TableCell>
              <TableCell sx={{ ...cellSx, color: C.muted }} align="right">{r.uo}</TableCell>
              <TableCell sx={{ ...cellSx, color: C.muted }} align="right">{fmtYoY(r.yoy)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  </Panel>
)

// Faint rule marking the start of each container-type column group.
const groupStartSx = { borderLeft: "1px solid #F1F3F7" }
const groupHeadSx = { ...headCellSx, color: C.text, textAlign: "center", pb: 0.25 }
const subHeadSx = { ...headCellSx, fontSize: 10 }

export const InboundReturnsTable = ({ data }) => (
  <Panel>
    <PanelHeader title="Inbound & returns detail" subtitle="Closed receipts and returns · by received/closed date" />
    {data.inboundReturnsDetail.length === 0 ? (
      <Typography variant="body2" sx={{ color: C.muted, py: 3, textAlign: "center" }}>
        No inbound receipt data for this period.
      </Typography>
    ) : (
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, borderBottom: "none" }} />
              <TableCell sx={groupHeadSx} colSpan={2}>LCL</TableCell>
              <TableCell sx={{ ...groupHeadSx, ...groupStartSx }} colSpan={2}>20ft FCL</TableCell>
              <TableCell sx={{ ...groupHeadSx, ...groupStartSx }} colSpan={2}>40ft FCL</TableCell>
              <TableCell sx={{ ...groupHeadSx, ...groupStartSx }}>Returns</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={subHeadSx}>Month</TableCell>
              <TableCell sx={subHeadSx} align="right">Shipments</TableCell>
              <TableCell sx={subHeadSx} align="right">Units</TableCell>
              <TableCell sx={{ ...subHeadSx, ...groupStartSx }} align="right">Shipments</TableCell>
              <TableCell sx={subHeadSx} align="right">Units</TableCell>
              <TableCell sx={{ ...subHeadSx, ...groupStartSx }} align="right">Shipments</TableCell>
              <TableCell sx={subHeadSx} align="right">Units</TableCell>
              <TableCell sx={{ ...subHeadSx, ...groupStartSx }} align="right">Units</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.inboundReturnsDetail.map(r => (
              <TableRow key={r.label}>
                <MonthCell row={r} />
                <TableCell sx={cellSx} align="right">{fmt(r.lcl)}</TableCell>
                <TableCell sx={cellSx} align="right">{fmt(r.lclUnits)}</TableCell>
                <TableCell sx={{ ...cellSx, ...groupStartSx }} align="right">{fmt(r.fcl20)}</TableCell>
                <TableCell sx={cellSx} align="right">{fmt(r.fcl20Units)}</TableCell>
                <TableCell sx={{ ...cellSx, ...groupStartSx }} align="right">{fmt(r.fcl40)}</TableCell>
                <TableCell sx={cellSx} align="right">{fmt(r.fcl40Units)}</TableCell>
                <TableCell sx={{ ...cellSx, ...groupStartSx }} align="right">{fmt(r.returnsUnits)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
  </Panel>
)
